import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { FaChevronLeft, FaChevronRight, FaShieldAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import { Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/images/medrpha_logo.png";

export default function SpotlightSlider() {
  const [bannerUrls, setBannerUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imgErrors, setImgErrors] = useState({});
  const navigate = useNavigate();
  const timerRef = useRef(null);

  // Fetch Category 1 banners strictly matching Flutter App (CustomHomeBoxes)
  useEffect(() => {
    let isMounted = true;

    const fetchBanners = async () => {
      try {
        setLoading(true);
        const res = await axios.get("https://admin.medrphalabs.com/api/Rating/GetAllBanner");
        const list = Array.isArray(res.data) ? res.data : [];

        // Exact Flutter logic from custom_home_boxes.dart:
        // b.bannerCategoryId == 1 && b.image != null && b.image!.isNotEmpty
        const seenUrls = new Set();
        const urls = [];

        list.forEach((b) => {
          if (Number(b.bannerCategoryId) === 1 && b.image && typeof b.image === "string") {
            const raw = b.image.trim();
            if (raw !== "") {
              let fullUrl = "";
              if (raw.startsWith("http")) {
                fullUrl = raw;
              } else if (raw.startsWith("BannerImage/")) {
                fullUrl = `https://admin.medrphalabs.com/${raw}`;
              } else {
                fullUrl = `https://admin.medrphalabs.com/BannerImage/${raw}`;
              }

              // Strict unique check by URL
              if (!seenUrls.has(fullUrl)) {
                seenUrls.add(fullUrl);
                urls.push({
                  id: b.id,
                  url: fullUrl,
                  title: b.title || "Medrpha Diagnostic Health Packages",
                });
              }
            }
          }
        });

        if (isMounted) {
          setBannerUrls(urls);
        }
      } catch (err) {
        console.error("Banner fetch error:", err);
        if (isMounted) setBannerUrls([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBanners();

    return () => {
      isMounted = false;
    };
  }, []);

  // Continuous Autoplay Slider (runs automatically every 3.5s)
  const startAutoplay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (bannerUrls.length <= 1) return;

    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % bannerUrls.length);
    }, 3500);
  }, [bannerUrls.length]);

  useEffect(() => {
    startAutoplay();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startAutoplay]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? bannerUrls.length - 1 : prev - 1));
    startAutoplay();
  }, [bannerUrls.length, startAutoplay]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % bannerUrls.length);
    startAutoplay();
  }, [bannerUrls.length, startAutoplay]);

  if (loading) {
    return (
      <div className="w-full h-[180px] sm:h-[240px] md:h-[320px] lg:h-[380px] bg-slate-100 animate-pulse rounded-2xl md:rounded-3xl" />
    );
  }

  // Fallback when no banners
  if (bannerUrls.length === 0) {
    return (
      <div className="relative w-full rounded-2xl md:rounded-3xl overflow-hidden shadow-xs bg-gradient-to-r from-red-600 via-rose-600 to-amber-600 text-white p-6 md:p-12 min-h-[260px] md:min-h-[360px] lg:min-h-[380px] flex flex-col justify-center">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
          <div className="max-w-2xl text-left">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3.5 py-1.5 rounded-full text-xs font-bold mb-3 border border-white/20">
              <Sparkles size={14} className="text-amber-300" />
              <span>MEDRPHA LABS DIAGNOSTICS & CARE</span>
            </div>
            <h2 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
              Comprehensive Full Body Health Packages at Flat 70% OFF
            </h2>
            <p className="mt-2.5 text-xs sm:text-sm md:text-base text-red-50 leading-relaxed font-medium">
              NABL-Accredited Labs • 60+ Vital Parameters • Free Sample Home Collection • 6-Hour Fast Reports on WhatsApp.
            </p>

            <div className="flex flex-wrap items-center gap-3.5 mt-6">
              <button
                onClick={() => navigate("/all-packages")}
                className="px-6 py-3 bg-white text-red-600 rounded-full text-xs md:text-sm font-black hover:bg-red-50 transition-all shadow-md flex items-center gap-2 cursor-pointer"
              >
                <span>Book Health Checkup</span>
                <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate("/all-tests")}
                className="px-5 py-3 bg-red-900/40 hover:bg-red-900/60 border border-white/30 text-white rounded-full text-xs md:text-sm font-bold transition-all backdrop-blur-md cursor-pointer"
              >
                Explore 100+ Tests
              </button>
            </div>
          </div>

          <div className="shrink-0 bg-white/95 backdrop-blur-md p-6 rounded-3xl border border-white/40 shadow-xl flex flex-col items-center justify-center text-center max-w-[240px]">
            <img src={logo} alt="Medrpha Labs" className="h-14 object-contain mb-2" />
            <span className="text-xs font-extrabold text-slate-800">Medrpha Diagnostics</span>
            <span className="text-[10px] font-semibold text-emerald-600 mt-0.5">100% NABL Certified Partner</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-8 text-xs text-white/95 font-semibold border-t border-white/20 pt-4">
          <span className="flex items-center gap-1.5"><FaShieldAlt className="text-emerald-300" /> 100% NABL Certified</span>
          <span className="flex items-center gap-1.5"><FaClock className="text-amber-300" /> 6-Hr Digital Reports</span>
          <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-300" /> Free Doctor Review</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-full rounded-2xl md:rounded-3xl overflow-hidden shadow-xs border border-slate-200/80 bg-slate-900 group">
      {/* Slides Container matching Flutter PageView with smooth transition */}
      <div className="relative w-full h-[180px] sm:h-[240px] md:h-[320px] lg:h-[380px] overflow-hidden">
        {bannerUrls.map((item, index) => {
          const isActive = index === currentIndex;
          const hasError = imgErrors[index];

          return (
            <div
              key={item.id || index}
              className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${
                isActive ? "opacity-100 z-10 pointer-events-auto" : "opacity-0 z-0 pointer-events-none"
              }`}
            >
              {!hasError ? (
                <img
                  src={item.url}
                  alt={item.title}
                  loading={index === 0 ? "eager" : "lazy"}
                  onError={() => setImgErrors((prev) => ({ ...prev, [index]: true }))}
                  className="w-full h-full object-fill block"
                />
              ) : (
                <div className="w-full h-full flex flex-col sm:flex-row items-center justify-center gap-6 p-6 bg-gradient-to-r from-red-600 to-rose-600 text-white text-center sm:text-left">
                  <div className="bg-white p-4 rounded-2xl shadow-md shrink-0">
                    <img src={logo} alt="Medrpha Labs" className="h-12 sm:h-14 object-contain" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-2xl font-black text-white">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-red-100 mt-1 font-medium">
                      Doorstep Sample Collection • Verified Digital Reports in 6 Hours
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows (Only shown when multiple banners exist) */}
      {bannerUrls.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 bg-white/90 backdrop-blur-md shadow-lg rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 focus:outline-none border border-slate-100 cursor-pointer"
            aria-label="Previous"
          >
            <FaChevronLeft className="text-red-600 text-sm md:text-base" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 bg-white/90 backdrop-blur-md shadow-lg rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 focus:outline-none border border-slate-100 cursor-pointer"
            aria-label="Next"
          >
            <FaChevronRight className="text-red-600 text-sm md:text-base" />
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
            {bannerUrls.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  i === currentIndex ? "w-6 bg-red-600" : "w-2 bg-white/70 hover:bg-white"
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}